import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaGithub } from 'react-icons/fa';
import { SplitText, FadeInText } from './AnimatedText';
import ParticleBackground from './ParticleBackground';
import AuroraBackground from './AuroraBackground';
import portfolioData from '../data/portfolio.json';
import './Projects.css';

const Projects = () => {
    const projects = portfolioData.projects;
    const [filteredProjects, setFilteredProjects] = useState(projects);
    const [selectedCategory, setSelectedCategory] = useState('all');

    const categories = ['all', 'fullstack', 'ml', 'mobile', 'algorithms'];

    const filterProjects = (category) => {
        setSelectedCategory(category);
        if (category === 'all') {
            setFilteredProjects(projects);
        } else {
            const filtered = projects.filter(project => {
                const techStack = project.technologies.join(' ').toLowerCase();
                const description = project.description.toLowerCase();
                switch (category) {
                    case 'fullstack':
                        // Full Stack: Has both frontend (React, JavaScript, HTML, CSS, Node.js) AND backend (Python, Flask, Node, SQL, FastAPI, PostgreSQL, Java backend)
                        const hasFrontend = techStack.includes('react') || techStack.includes('javascript') || techStack.includes('html') || techStack.includes('css') || techStack.includes('node.js') || techStack.includes('typescript');
                        const hasBackend = techStack.includes('python') || techStack.includes('flask') || techStack.includes('node') || techStack.includes('sql') || techStack.includes('fastapi') || techStack.includes('postgresql') || techStack.includes('java') || techStack.includes('backend') || techStack.includes('saas');
                        return hasFrontend && hasBackend;
                    case 'ml':
                        // Machine Learning: Deep Learning, ML libraries, or ML-related terms
                        return techStack.includes('deep learning') || techStack.includes('machine learning') || techStack.includes('ml') || 
                               techStack.includes('scikit-learn') || techStack.includes('pandas') || techStack.includes('numpy') ||
                               techStack.includes('pytorch') || techStack.includes('tensorflow') || techStack.includes('logistic regression') ||
                               techStack.includes('standardscaler') || techStack.includes('tcn') || techStack.includes('resnet') ||
                               description.includes('deep learning') || description.includes('machine learning') || description.includes('ml') ||
                               description.includes('neural network') || description.includes('classification');
                    case 'mobile':
                        // Mobile: React Native, Flutter, Android, Kotlin, iOS, Swift
                        return techStack.includes('react native') || techStack.includes('flutter') || techStack.includes('mobile') ||
                               techStack.includes('android') || techStack.includes('kotlin') || techStack.includes('ios') || techStack.includes('swift');
                    case 'algorithms':
                        // Algorithms: Data structures, algorithms, compilers, AST manipulation
                        return techStack.includes('algorithm') || techStack.includes('data structures') || techStack.includes('ast') ||
                               techStack.includes('compiler') || techStack.includes('obfuscation') || techStack.includes('aho-corasick') ||
                               techStack.includes('trie') || techStack.includes('string matching') || techStack.includes('pattern matching') ||
                               description.includes('algorithm') || description.includes('data structure') || description.includes('pattern matching');
                    default:
                        return true;
                }
            });
            setFilteredProjects(filtered);
        }
    };

    return (
        <div className="projects">
            <AuroraBackground />
            <ParticleBackground particleCount={30} />
            <div className="container">
                {/* Header */}
                <motion.section
                    className="projects-header"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1 className="page-title">
                        <SplitText delay={0.2}>My Projects</SplitText>
                    </h1>
                    <p className="page-subtitle">
                        <FadeInText delay={0.4}>
                            Here's a collection of my recent work and personal projects
                        </FadeInText>
                    </p>
                </motion.section>

                {/* Filter Buttons */}
                <motion.section
                    className="filter-section"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <div className="filter-buttons">
                        {categories.map((category) => {
                            const categoryLabels = {
                                'all': 'All',
                                'fullstack': 'Full Stack',
                                'ml': 'Machine Learning',
                                'mobile': 'Mobile',
                                'algorithms': 'Algorithms'
                            };
                            return (
                                <motion.button
                                    key={category}
                                    className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                                    onClick={() => filterProjects(category)}
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {categoryLabels[category] || category.charAt(0).toUpperCase() + category.slice(1)}
                                </motion.button>
                            );
                        })}
                    </div>
                </motion.section>

                {/* Projects Grid */}
                <motion.section
                    className="projects-grid-section"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    {filteredProjects.length === 0 ? (
                        <div className="no-projects">
                            <p>No projects found for the selected category.</p>
                        </div>
                    ) : (
                        <div className="projects-grid">
                            {filteredProjects.map((project, index) => (
                                <motion.div
                                    key={project.id}
                                    className="project-card"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    whileHover={{ y: -10 }}
                                >
                                    <div className="project-image">
                                        <div className="image-placeholder">
                                            <span>📱</span>
                                        </div>
                                        {project.github && project.github !== '#' && (
                                            <div className="project-overlay">
                                                <div className="project-actions">
                                                    <motion.a
                                                        href={project.github}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="action-btn"
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                    >
                                                        <FaGithub />
                                                    </motion.a>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="project-content">
                                        <h3 className="project-title">{project.title}</h3>
                                        <p className="project-description">{project.description}</p>

                                        <div className="project-technologies">
                                            {project.technologies.map((tech) => (
                                                <span key={tech} className="tech-tag">{tech}</span>
                                            ))}
                                        </div>

                                        <div className="project-features">
                                            <h4>Key Features:</h4>
                                            <ul>
                                                {project.features.slice(0, 3).map((feature, idx) => (
                                                    <li key={idx}>{feature}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </motion.section>
            </div>
        </div>
    );
};

export default Projects; 